import {
  Button,
  ComposedModal,
  InlineLoading,
  InlineNotification,
  ModalBody,
  ModalFooter,
  ModalHeader,
  RadioButton,
  RadioButtonGroup,
} from '@carbon/react';
import { useAtom, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';

import { SuccessToast } from 'components/success-toast/success-toast';
import { format8 } from 'utils/number';

import {
  promotionPendingAtom,
  reconcileApplyLoadableAtom,
  reconcileModalOpenAtom,
  reconcilePreviewLoadableAtom,
  type ReconcileEntry,
  type ReconcileResolution,
  type ReconcileValue,
} from './atoms';
import { getMutationErrorMessage } from './utils';
import { useWorkspaceConfirmation } from './workspace-confirmation-context';
import styles from './reconcile-modal.module.scss';

const resolutionKey = (
  slotId: string,
  stream: string,
  subtractId: string | null,
  uatId: number | null
): string => `${slotId}::${stream}::${subtractId ?? ''}::${uatId ?? ''}`;

// Compact one-line summary of a reconcile value (mine / promoted side).
const describeValue = (value: ReconcileValue | null): string => {
  if (!value || !value.confirmed) return 'Not certified';
  const nri =
    typeof value.confirmedNri === 'number' ? format8(value.confirmedNri) : '—';
  const wi =
    typeof value.confirmedWi === 'number' ? format8(value.confirmedWi) : '—';
  return `NRI ${nri} · WI ${wi}`;
};

/**
 * Reconcile (pull-in) modal (INS-9740). Previews the 3-way merge against the
 * entity's certified master, auto-applies untouched slots, and lets the user
 * resolve each touched-but-divergent slot (Keep mine / Take promoted). Applying
 * refetches the table; the pending banner clears on success.
 *
 * @return {JSX.Element} The modal (hidden when closed).
 */
export const ReconcileModal = (): JSX.Element => {
  const [open, setOpen] = useAtom(reconcileModalOpenAtom);
  const { workspaceId } = useWorkspaceConfirmation();
  const setPending = useSetAtom(promotionPendingAtom);

  const [previewLoadable, fetchPreview] = useAtom(reconcilePreviewLoadableAtom);
  const [applyLoadable, applyReconcile] = useAtom(reconcileApplyLoadableAtom);

  // Conflict resolutions default to 'keep' — never silently overwrite work the
  // user did in this workspace; they explicitly opt into 'master' per slot.
  const [choices, setChoices] = useState<Record<string, 'keep' | 'master'>>({});
  const [error, setError] = useState<string | null>(null);
  const [successVisible, setSuccessVisible] = useState(false);
  // reconcilePreviewLoadableAtom is a module-global, so its hasData lingers
  // after close. Gate on a per-open `loaded` flag so a reopen never renders the
  // previous run's preview for a frame before this open's fetch settles.
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open) {
      setLoaded(false);
      return undefined;
    }
    setError(null);
    setChoices({});
    setLoaded(false);

    let cancelled = false;
    fetchPreview(workspaceId)
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, workspaceId, fetchPreview]);

  const applying = applyLoadable.state === 'loading';
  const previewLoading = !loaded || previewLoadable.state === 'loading';
  const previewErrored = loaded && previewLoadable.state === 'hasError';
  const preview =
    loaded && previewLoadable.state === 'hasData' ? previewLoadable.data : null;

  const conflicts: ReconcileEntry[] = preview?.conflicts ?? [];
  const autoCount = preview?.autoAppliable.length ?? 0;

  const choiceFor = (entry: ReconcileEntry): 'keep' | 'master' =>
    choices[
      resolutionKey(
        entry.slotId,
        entry.stream,
        entry.subtractId,
        entry.unitAllocationTractId
      )
    ] ?? 'keep';

  const handleClose = (): void => {
    if (applying) return;
    setOpen(false);
  };

  const handleApply = async (): Promise<void> => {
    if (applying) return;
    setError(null);
    const resolutions: ReconcileResolution[] = conflicts.map((entry) => ({
      slot_id: entry.slotId,
      stream: entry.stream,
      subtract_id: entry.subtractId,
      unit_allocation_tract_id: entry.unitAllocationTractId,
      choice: choiceFor(entry),
    }));

    try {
      await applyReconcile(workspaceId, resolutions);
      setPending(null);
      setOpen(false);
      setSuccessVisible(true);
    } catch (err) {
      setError(getMutationErrorMessage(err, "Couldn't pull in the changes."));
    }
  };

  const nothingToApply =
    preview !== null && autoCount === 0 && conflicts.length === 0;

  return (
    <>
      <ComposedModal open={open} onClose={handleClose} size="lg">
        <ModalHeader title="Pull in certified changes" />
        <ModalBody>
          {previewLoading && (
            <InlineLoading description="Comparing with the latest certification…" />
          )}

          {previewErrored && (
            <InlineNotification
              kind="error"
              lowContrast
              hideCloseButton
              title="Couldn't load the comparison."
            />
          )}

          {preview && (
            <>
              {nothingToApply && preview.informational.length === 0 && (
                <p className={styles.summary}>
                  This workspace already matches the latest certification.
                  Nothing to pull in.
                </p>
              )}

              {autoCount > 0 && (
                <p className={styles.summary}>
                  {autoCount} slot{autoCount === 1 ? '' : 's'} you haven&apos;t
                  edited will update to the latest certified values.
                </p>
              )}

              {conflicts.length > 0 && (
                <div className={styles.conflicts}>
                  <h3 className={styles.conflictsHeading}>
                    {conflicts.length} slot{conflicts.length === 1 ? '' : 's'}{' '}
                    you changed differ — choose which to keep
                  </h3>
                  {conflicts.map((entry) => {
                    const key = resolutionKey(
                      entry.slotId,
                      entry.stream,
                      entry.subtractId,
                      entry.unitAllocationTractId
                    );
                    return (
                      <div key={key} className={styles.conflictRow}>
                        <RadioButtonGroup
                          name={key}
                          legendText={`${entry.slotId} · ${entry.stream}${
                            entry.subtractId ? ` · ${entry.subtractId}` : ''
                          }${
                            entry.unitAllocationTractId
                              ? ` · UAT ${entry.unitAllocationTractId}`
                              : ''
                          }`}
                          orientation="vertical"
                          valueSelected={choiceFor(entry)}
                          onChange={(value) =>
                            setChoices((prev) => ({
                              ...prev,
                              [key]: value as 'keep' | 'master',
                            }))
                          }
                        >
                          <RadioButton
                            labelText={`Keep mine — ${describeValue(
                              entry.workspaceValue
                            )}`}
                            value="keep"
                          />
                          <RadioButton
                            labelText={`Take promoted — ${describeValue(
                              entry.masterValue
                            )}`}
                            value="master"
                          />
                        </RadioButtonGroup>
                      </div>
                    );
                  })}
                </div>
              )}

              {preview.informational.length > 0 && (
                <p className={styles.summary}>
                  {preview.informational.length} certified slot
                  {preview.informational.length === 1 ? '' : 's'} are no longer
                  in this workspace and won&apos;t be changed here.
                </p>
              )}

              {error && (
                <InlineNotification
                  className={styles.notification}
                  kind="error"
                  lowContrast
                  hideCloseButton
                  title={error}
                />
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button kind="secondary" onClick={handleClose} disabled={applying}>
            Cancel
          </Button>
          <Button
            kind="primary"
            onClick={handleApply}
            disabled={applying || preview === null || nothingToApply}
          >
            {applying ? 'Pulling in…' : 'Pull in changes'}
          </Button>
        </ModalFooter>
      </ComposedModal>
      {successVisible && (
        <SuccessToast
          message="Certified changes pulled in"
          onClose={() => {
            setSuccessVisible(false);
            return false;
          }}
        />
      )}
    </>
  );
};
